import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const STATUS_OPTIONS = [
  'pending',
  'in_review',
  'reviewed',
  'rejected',
  'accepted',
];

export default function EditorPanel({ user }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewers, setReviewers] = useState([]);
  const [assigning, setAssigning] = useState(null);
  const [showReviewerDropdown, setShowReviewerDropdown] = useState(null);

  useEffect(() => {
    if (user && user.role === 'editor') {
      fetchSubmissions();
      fetchReviewers();
    }
    // eslint-disable-next-line
  }, [user]);

  async function fetchSubmissions() {
    setLoading(true);
    const { data } = await supabase
      .from('request')
      .select('*')
      .eq('editor_id', user.id)
      .eq('sent_to_editor', true);
    setSubmissions(data || []);
    setLoading(false);
  }

  async function fetchReviewers() {
    const { data } = await supabase
      .from('users')
      .select('id, full_name, username, email')
      .eq('role', 'reviewer');
    setReviewers(data || []);
  }

  async function handleSendToReviewer(requestId, reviewerId) {
    setAssigning(requestId);
    await supabase
      .from('request')
      .update({ reviewer_id: reviewerId, sent_to_reviewer: true })
      .eq('id', requestId);
    await fetchSubmissions();
    setAssigning(null);
    setShowReviewerDropdown(null);
  }

  if (!user || user.role !== 'editor') {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger">You are not authorized to access the editor panel.</div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Editor Panel</h2>
      <div className="card shadow-lg border-0 rounded-4">
        <div className="card-body p-4">
          <h4 className="mb-4">Assigned Submissions</h4>
          {loading ? (
            <div className="text-center my-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
          ) : submissions.length === 0 ? (
            <div className="alert alert-info">No submissions assigned yet.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Submitter</th>
                    <th>Status</th>
                    <th>Submitted At</th>
                    <th>PDF</th>
                    <th>Send to Reviewer</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(sub => (
                    <tr key={sub.id}>
                      <td>{sub.title}</td>
                      <td>{sub.username || sub.email || '-'}</td>
                      <td>{sub.status}</td>
                      <td>{sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ''}</td>
                      <td>
                        {sub.pdf_url ? (
                          <a href={sub.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger btn-sm">Download PDF</a>
                        ) : (
                          <span className="text-muted">No PDF</span>
                        )}
                      </td>
                      <td>
                        {sub.reviewer_id ? (
                          <span className="badge bg-success">Sent</span>
                        ) : showReviewerDropdown === sub.id ? (
                          <div className="d-flex align-items-center gap-2">
                            <select className="form-select form-select-sm" style={{minWidth: 120}} id={`reviewer-select-${sub.id}`}
                              defaultValue="" onChange={e => handleSendToReviewer(sub.id, e.target.value)} disabled={assigning === sub.id}>
                              <option value="" disabled>Select Reviewer</option>
                              {reviewers.map(r => (
                                <option value={r.id} key={r.id}>{r.full_name || r.username || r.email}</option>
                              ))}
                            </select>
                            <button className="btn btn-secondary btn-sm" onClick={() => setShowReviewerDropdown(null)}>Cancel</button>
                          </div>
                        ) : (
                          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowReviewerDropdown(sub.id)} disabled={assigning === sub.id}>
                            Send
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 